import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { User } from "lucide-react";
import FollowUserButton from "@/components/FollowUserButton";
import SocialsDisplay from "@/components/SocialsDisplay";
import ContactsDisplay from "@/components/ContactsDisplay";
import { savedNearZip } from "@/lib/filterCookies";


const contactCardCls = "inline-flex items-center gap-2.5 px-3 py-2 bg-[#0f0f0f] border border-[#2a2a2a] hover:bg-[#181818] transition-colors group cursor-pointer text-left";
const EXAMPLE_NOTE = "This is an example profile and can't be followed or contacted.";
const notifyExample = () => toast.info(EXAMPLE_NOTE);

export default function PlayerDetail() {
    const { id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const nearZip = savedNearZip();

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login"); return; }
        api.get(`/players/${id}`, { params: nearZip ? { near_zip: nearZip } : {} })
            .then((r) => setPlayer(r.data))
            .catch(() => setPlayer(null))
            .finally(() => setLoading(false));
    }, [id, user, authLoading, navigate, nearZip]);

    if (loading || authLoading) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    if (!player) return (
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
            <div className="font-display text-4xl">Player not found</div>
            <Link to="/" className="text-[#a855f7] text-sm uppercase tracking-wider mt-4 inline-block">← Back to home</Link>
        </div>
    );

    const heightFt = Math.floor(player.height_inches / 12);
    const heightIn = player.height_inches % 12;

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-8" data-testid="player-detail-page">
            <div className="mt-2">
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex flex-col gap-2 shrink-0 w-32 sm:w-40">
                        <PlayerPhoto src={player.athlete_photo} name={player.name} />
                        {player.username && (
                            player.is_example ? (
                                <div data-testid="player-profile-card" title={EXAMPLE_NOTE} onClick={notifyExample} className="block px-3 py-2 w-full bg-[#0f0f0f] border border-[#2a2a2a] hover:bg-[#181818] transition-colors group text-center cursor-pointer">
                                    <span className="block text-[9px] uppercase tracking-[0.14em] text-[#666] leading-none mb-1.5 whitespace-nowrap">{player.athlete_relation === "guardian" ? "Parent/Guardian" : "Athlete"}</span>
                                    <span className="flex items-center justify-center gap-2.5">
                                        <span className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center shrink-0" style={{ background: "#a855f71f", color: "#a855f7" }}>
                                            {player.photo ? <img src={player.photo} alt="" className="w-full h-full object-cover" /> : <User className="h-3.5 w-3.5" />}
                                        </span>
                                        <span className="text-sm text-white truncate group-hover:text-[#a855f7] transition-colors">{player.username}</span>
                                        <FollowUserButton userId={player.id} name={player.username} example />
                                    </span>
                                </div>
                            ) : (
                            <Link to={`/u/${player.username}`} data-testid="player-profile-card" className="block px-3 py-2 w-full bg-[#0f0f0f] border border-[#2a2a2a] hover:bg-[#181818] transition-colors group text-center">
                                <span className="block text-[9px] uppercase tracking-[0.14em] text-[#666] leading-none mb-1.5 whitespace-nowrap">{player.athlete_relation === "guardian" ? "Parent/Guardian" : "Athlete"}</span>
                                <span className="flex items-center justify-center gap-2.5">
                                    <span className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center shrink-0" style={{ background: "#a855f71f", color: "#a855f7" }}>
                                        {player.photo ? <img src={player.photo} alt="" className="w-full h-full object-cover" /> : <User className="h-3.5 w-3.5" />}
                                    </span>
                                    <span className="text-sm text-white truncate group-hover:text-[#a855f7] transition-colors">{player.username}</span>
                                    <FollowUserButton userId={player.id} name={player.username} />
                                </span>
                            </Link>
                            )
                        )}
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                        <div className="text-[#a855f7] text-xs uppercase tracking-[0.3em]">{player.position} · Class of {player.grad_year}</div>
                        {player.is_example && <span className="inline-block mt-2 bg-[#a855f7] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm" data-testid="player-example-badge">● Example Profile</span>}
                        <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mt-2 break-words" data-testid="player-name">{player.name}</h1>

                        <dl className="mt-5 w-fit max-w-full divide-y divide-[#2a2a2a] border-y border-[#2a2a2a]" data-testid="player-info-list">
                            <InfoRow label="Height" value={`${heightFt}'${heightIn}"`} testid="player-info-height" />
                            <InfoRow label="Age" testid="player-info-age" value={player.age != null ? `${player.age}` : "Not listed"} />
                            {player.age_as_of_2027 != null && (
                                <InfoRow label="Age as of 01AUG2027" value={`${player.age_as_of_2027}`} testid="player-info-age-asof" />
                            )}
                            <InfoRow label="School" value={player.school || "Not listed in their profile"} muted={!player.school} testid="player-info-school" />
                            <InfoRow label="Zip code" value={player.home_zip} testid="player-info-zip" />
                            <InfoRow label="Travel Distance" value={`${player.max_travel_distance} mi`} testid="player-info-travel" />
                            {nearZip && player.distance_miles != null && (
                                <InfoRow
                                    label={`Distance from ${nearZip}`}
                                    value={`${Math.round(player.distance_miles)} mi`}
                                    testid="player-info-distance"
                                    valueClass={player.distance_miles > player.max_travel_distance ? "text-red-500 font-semibold" : ""}
                                />
                            )}
                        </dl>
                    </div>
                </div>

                {player.bio && (
                    <div className="mt-8">
                        <h2 className="font-display text-2xl tracking-wide mb-3">About</h2>
                        <p className="text-[#cfcfcf] leading-relaxed">{player.bio}</p>
                    </div>
                )}

                <div className="mt-8" data-testid="player-contact-section">
                    <h2 className="font-display text-2xl tracking-wide mb-3">Contact / Social Media</h2>
                    <div className="flex flex-wrap gap-2">
                        {player.is_example ? (
                            <button
                                type="button"
                                data-testid="player-message-card"
                                title={EXAMPLE_NOTE}
                                onClick={notifyExample}
                                className={contactCardCls}
                            >
                                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-black">
                                    <img src="/tsi-mark.png" alt="" className="w-full h-full object-contain p-0.5" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm text-white truncate max-w-[15rem] group-hover:text-[#a855f7] transition-colors">Direct Message</span>
                                </span>
                            </button>
                        ) : user && user.id !== player.id && (
                            <Link
                                to={`/feed?tab=messages&u=${player.id}&prefill=${encodeURIComponent(`Hi @${player.username || player.name}, I saw your athlete profile on Travel Sports Info and wanted to connect.`)}`}
                                data-testid="player-message-card"
                                className={contactCardCls}
                            >
                                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-black">
                                    <img src="/tsi-mark.png" alt="" className="w-full h-full object-contain p-0.5" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm text-white truncate max-w-[15rem] group-hover:text-[#a855f7] transition-colors">Direct Message</span>
                                </span>
                            </Link>
                        )}
                        {player.contacts?.length
                            ? <ContactsDisplay contacts={player.contacts} testidPrefix="player-contact" hideTitle inline />
                            : player.socials ? <SocialsDisplay socials={player.socials} testIdPrefix="player-socials-display" /> : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ label, value, muted, testid, valueClass }) {
    return (
        <div className="flex items-baseline gap-3 py-2.5">
            <dt className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] w-32 shrink-0">{label}</dt>
            <dd className={`text-sm ${valueClass ? valueClass : (muted ? "text-[#6b6b6b] italic" : "text-white")}`} data-testid={testid}>{value}</dd>
        </div>
    );
}

function PlayerPhoto({ src, name }) {
    const [failed, setFailed] = useState(false);
    const letter = (name || "?").trim()[0]?.toUpperCase() || "?";
    return (
        <div className="w-full aspect-[4/5] shrink-0 border border-[#2a2a2a] overflow-hidden bg-[#0f0f0f]" data-testid="player-photo">
            {src && !failed ? (
                <img src={src} alt={name} onError={() => setFailed(true)} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-5xl text-[#a855f7]">{letter}</div>
            )}
        </div>
    );
}
